import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Text, FlexContainer, Inputs, Chip, Banner, Sizes, Buttons } from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import { showModalAction } from 'Store/modules/app/actions';
import useTranslation from 'Hooks/useTranslation';
import { TaxReturnTypes } from 'Containers/Result/utils';
import {
  useAutoRenewal,
  usePatchFilingUser,
  useSubmitDeclarationCrossSell,
  useSubscribeLiteFlex,
  useGetPreviousYearInfo,
} from 'Containers/Filing/hooks';
import { useFetchGetPlans } from 'Containers/Plans/hooks';
import ToPayInformation from 'Containers/PreFiling/components/ToPayInformation';
import { getIsPayLater } from 'Components/Modal/components/PaymentMethodPY/utils';
import { TAX_YEAR } from 'Config';
import { FRONT_PLANS, parsePlans } from 'Constants/plans';
import * as Styled from './styles';
import { proModalOptions, YearsBannerProps } from './types';
import { handleYearsBannerClick } from './utils';

const BREAK_AMOUNT = 100;
const CONTEXT_LESS_AMOUNT = 'lessAmount';

export const YearsBanner = ({
  data,
  compensation,
  isCompensationAndPay,
  isForCompensation,
}: YearsBannerProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.FILING);
  const pyInfo = useGetPreviousYearInfo();
  const [isActive, setIsActive] = useState(true);
  const dispatch = useDispatch();
  const { data: plans, isLoading } = useFetchGetPlans();
  const { mutate: submitInfo } = usePatchFilingUser();
  const { mutate } = useSubscribeLiteFlex();
  const { mutate: autoRenewal } = useAutoRenewal();
  const isPayLater = getIsPayLater(Number(pyInfo?.amountPending));
  const { mutate: submitDeclaration } = useSubmitDeclarationCrossSell(!isPayLater);

  const resultIsLessThan100 =
    data?.taxReturnType === TaxReturnTypes.TO_RETURN &&
    data?.taxReturnResult &&
    Math.abs(data?.taxReturnResult) < BREAK_AMOUNT;

  const handleChange = (): void => {
    setIsActive(!isActive);
  };

  const tContext =
    (resultIsLessThan100 || data?.mandatory) && !isForCompensation && CONTEXT_LESS_AMOUNT;

  const handleClick = (): void => {
    if (isCompensationAndPay && compensation) {
      const filterPlanPro = plans?.find(item => parsePlans(item.name) === FRONT_PLANS.PRO);
      dispatch(
        showModalAction({
          options: {
            ...proModalOptions,
            data: {
              ...proModalOptions.data,
              upsellingProps: filterPlanPro,
            },
          },
        })
      );
    } else {
      handleYearsBannerClick({
        isActive,
        data,
        mutate,
        autoRenewal,
        submitDeclaration,
        taxYear: TAX_YEAR,
        compensation,
        submitInfo,
        notIsPayLater: !isPayLater,
        isForCompensation,
      });
    }
  };

  return (
    <>
      <Styled.YearsBannerContainer
        fullWidth
        justify="center"
        align="center"
        $compensation={isCompensationAndPay}
      >
        {isCompensationAndPay ? (
          <ToPayInformation />
        ) : (
          <Styled.YearsBannerContent direction="column" gap="dimension_08" justify="flex-start">
            <Chip size="S" type="success_shaded">
              {t('PreFiling.years.detail.free')}
            </Chip>
            <FlexContainer justify="space-between" fullWidth align="center">
              <Text type="paragraphs" color="dark" size="S_L_S">
                {t('PreFiling.years.detail.info', { context: tContext })}
              </Text>
              <Inputs.Switcher value={isActive} onChange={handleChange} />
            </FlexContainer>
            {!isActive ? (
              <Banner
                id="years-banner"
                mode={data?.mandatory && !isForCompensation ? 'error' : 'cold'}
                layout="embed"
                position="horizontally"
                hasIcon
                label={
                  data?.mandatory && !isForCompensation
                    ? t('PreFiling.years.banner.title.mandatory', { context: data?.taxReturnType })
                    : t('PreFiling.years.banner.title', { context: data?.taxReturnType })
                }
                size={Sizes.S}
              />
            ) : (
              <>
                {resultIsLessThan100 && (
                  <FlexContainer justify="space-between" fullWidth align="center">
                    <Text type="paragraphs" color="dark" size="XS_S_S">
                      {t('PreFiling.less.amount.info', {
                        amount: YayoyakiFormatter.formatAmountMxn(Math.abs(data.taxReturnResult)),
                      })}
                    </Text>
                    <Text type="paragraphs" color="fresh" size="M_XL_S">
                      {t('PreFiling.less.amount.free')}
                    </Text>
                  </FlexContainer>
                )}
                <Text type="paragraphs" color="grey" size="XS_S_S">
                  {t('PreFiling.years.detail.description', { context: tContext })}
                </Text>
              </>
            )}
          </Styled.YearsBannerContent>
        )}
      </Styled.YearsBannerContainer>
      <Styled.ButtonsContainer align="center" justify="center" fullWidth>
        <Buttons.Classic
          mode="primary"
          size="m"
          label={t('PreFiling.buttons.next')}
          onClick={handleClick}
          disabled={isLoading && isCompensationAndPay}
        />
      </Styled.ButtonsContainer>
    </>
  );
};

export default YearsBanner;
